<script>
  import { _ } from '../../lib/i18n.js';
  import { severityColors, severityOrder, fallbackBadge } from '../../lib/complianceMaps.js';
  import { metricKind, isSeverityObject, humanizeKey, formatScalar } from '../../lib/complianceMetrics.js';
  import { ArrowRight } from 'lucide-svelte';

  // Free-form section.metrics, plus the originating section key so id-array
  // metrics can deep-link into the Findings tab instead of dumping CVE ids.
  export let metrics = null;
  export let sectionKey = '';
  // (sectionKey) => void — jumps to the Findings tab filtered by this section.
  export let onSeeFindings = null;

  // Array metrics that are sets of finding ids we can cross-link to the table.
  const ID_ARRAY_KEYS = new Set(['openVulnIds', 'containerVulnIds', 'failedRuleIds', 'vulnerableComponentIds']);
  const MAX_LIST = 50;

  $: entries = metrics && typeof metrics === 'object' ? Object.entries(metrics) : [];

  function severityEntries(obj) {
    // Render in canonical severity order, only the buckets that are present.
    return severityOrder.filter((s) => s in obj).map((s) => [s, obj[s]]);
  }
</script>

{#if entries.length}
  <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
    {#each entries as [key, value]}
      {@const kind = metricKind(value)}
      <div class="bg-gray-50 dark:bg-gray-800/50 rounded px-2 py-1.5 break-words">
        <div class="text-gray-400 mb-0.5 truncate" title={key}>{humanizeKey(key)}</div>

        {#if kind === 'empty'}
          <div class="font-mono text-gray-500">—</div>

        {:else if kind === 'bool'}
          <span class="font-mono font-bold {value ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}">
            {value ? '✓' : '✗'}
          </span>

        {:else if kind === 'scalar'}
          <div class="font-mono text-gray-700 dark:text-gray-200">{formatScalar(value)}</div>

        {:else if kind === 'object' && isSeverityObject(value)}
          <div class="flex flex-wrap gap-1">
            {#each severityEntries(value) as [sev, n]}
              <span class="px-1.5 py-0.5 text-[11px] font-bold border rounded {severityColors[sev] || fallbackBadge}">
                {n} {$_('complianceReports.severity.' + sev)}
              </span>
            {/each}
          </div>

        {:else if kind === 'object'}
          <div class="space-y-0.5">
            {#each Object.entries(value) as [subKey, subVal]}
              <div class="flex items-baseline justify-between gap-2">
                <span class="text-gray-400 truncate" title={subKey}>{humanizeKey(subKey)}</span>
                <span class="font-mono text-gray-700 dark:text-gray-200 shrink-0">{formatScalar(subVal)}</span>
              </div>
            {/each}
          </div>

        {:else if kind === 'array'}
          <div class="font-mono text-gray-700 dark:text-gray-200">{value.length}</div>
          {#if ID_ARRAY_KEYS.has(key) && onSeeFindings}
            <button
              type="button"
              class="mt-1 inline-flex items-center gap-1 text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
              on:click={() => onSeeFindings(sectionKey)}
            >
              {$_('complianceReports.metrics.seeFindings')} <ArrowRight size={11} />
            </button>
          {:else}
            <details class="mt-1">
              <summary class="cursor-pointer text-[11px] text-gray-500 select-none">
                {$_('complianceReports.metrics.elements', { values: { count: value.length } })}
              </summary>
              <ul class="mt-1 max-h-40 overflow-y-auto space-y-0.5">
                {#each value.slice(0, MAX_LIST) as item}
                  <li class="font-mono text-[11px] text-gray-600 dark:text-gray-300 break-all">{String(item)}</li>
                {/each}
                {#if value.length > MAX_LIST}
                  <li class="text-[11px] text-gray-400 italic">
                    {$_('complianceReports.metrics.more', { values: { count: value.length - MAX_LIST } })}
                  </li>
                {/if}
              </ul>
            </details>
          {/if}
        {/if}
      </div>
    {/each}
  </div>
{/if}
